import { Button, type FormInstance, message, Modal, Space, Tag } from 'antd';
import React, { type CSSProperties, useState } from 'react';
import { previewDisplayText } from '@/services/admin/dictionary';

export interface DisplayTextPreviewButtonProps {
  /** value 字段的值，用于生成预览 */
  value: string;
  /** 表单实例，用于设置 display_text 字段的值 */
  form?: FormInstance;
  /** display_text 字段的名称，默认为 'display_text' */
  displayTextFieldName?: string;
  /** 按钮文本，默认为 '自动生成多语言' */
  buttonText?: string;
  /** 按钮类型 */
  buttonType?: 'default' | 'primary' | 'dashed' | 'link' | 'text';
  /** 按钮大小 */
  buttonSize?: 'small' | 'middle' | 'large';
  /** 是否禁用 */
  disabled?: boolean;
  /** 按钮样式 */
  style?: CSSProperties;
  /** 目标语言列表（可选，默认：["zh_CN", "en_US", "km_KH"]） */
  targetLanguages?: string[];
  /** 应用后的回调函数 */
  onApply?: (displayText: Record<string, string>) => void;
}

/**
 * Display Text 预览按钮组件
 * 
 * 用于字典管理中的 display_text 自动生成预览功能
 * 
 * @example
 * ```tsx
 * <DisplayTextPreviewButton
 *   value="软件工程师"
 *   form={form}
 *   onApply={(displayText) => {
 *     setDisplayText(displayText);
 *   }}
 * />
 * ```
 */
const DisplayTextPreviewButton: React.FC<DisplayTextPreviewButtonProps> = (props) => {
  const {
    value,
    form,
    displayTextFieldName = 'display_text',
    buttonText = '自动生成多语言',
    buttonType = 'default',
    buttonSize = 'middle',
    disabled = false,
    style,
    targetLanguages,
    onApply,
  } = props;

  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewResult, setPreviewResult] = useState<API.DictionaryDisplayTextPreviewResponse | null>(null);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);

  const handlePreview = async () => {
    // 验证 value 不为空
    if (!value || !value.trim()) {
      message.error('请先填写 value 字段');
      return;
    }

    setPreviewLoading(true);
    try {
      const response = await previewDisplayText({
        value: value.trim(),
        target_languages: targetLanguages || undefined,
      });

      if (response.code === 0 && response.data) {
        setPreviewResult(response.data);
        setPreviewModalVisible(true);
      } else {
        message.error(response.message || '预览失败');
      }
    } catch (error: any) {
      message.error(error?.response?.data?.message || '预览失败，请重试');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleApply = () => {
    if (previewResult?.suggested_display_text) {
      // 调用回调函数
      if (onApply) {
        onApply(previewResult.suggested_display_text);
      }
      // 如果提供了表单实例，也设置表单值
      if (form) {
        form.setFieldsValue({ [displayTextFieldName]: previewResult.suggested_display_text });
      }
      message.success('已应用多语言文本');
      setPreviewModalVisible(false);
    }
  };

  const handleCancel = () => {
    setPreviewModalVisible(false);
    // 不清除 previewResult，以便用户再次打开时还能看到
  };

  return (
    <>
      <Button
        type={buttonType}
        size={buttonSize}
        loading={previewLoading}
        onClick={handlePreview}
        disabled={disabled || !value || !value.trim()}
        style={style}
      >
        {buttonText}
      </Button>

      <Modal
        title="多语言预览"
        open={previewModalVisible}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            取消
          </Button>,
          previewResult && (
            <Button
              key="apply"
              type="primary"
              onClick={handleApply}
            >
              应用
            </Button>
          ),
        ]}
        width={600}
      >
        {previewResult && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <strong>建议的多语言文本：</strong>
            </div>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              {Object.entries(previewResult.suggested_display_text).map(([lang, text]) => (
                <div key={lang}>
                  <Tag color="blue" style={{ minWidth: 80, textAlign: 'center' }}>
                    {lang === 'zh_CN' ? '中文' : lang === 'en_US' ? '英文' : lang === 'km_KH' ? '高棉语' : lang}
                  </Tag>
                  <span style={{ marginLeft: 8 }}>{text}</span>
                </div>
              ))}
            </Space>
            {previewResult.note && (
              <div style={{ marginTop: 16, padding: 12, background: '#f5f5f5', borderRadius: 4, fontSize: 12, color: '#666' }}>
                {previewResult.note}
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

export default DisplayTextPreviewButton;
